import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useState } from 'react';
import { useSearchParams } from 'react-router';

import { useCanaryFinalRecommendations } from '@/api/aiVisibility';
import { Empty } from '@/components/Empty';
import WidgetError from '@/components/errors/WidgetError';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/Form/pickers/primitives/select';
import { Skeleton } from '@/components/Skeleton';
import { Text, Title } from '@/components/typography';
import { cn } from '@/lib/utils';

import AIVisibilityRecommendationSheet from './AIVisibilityRecommendationSheet';
import RecommendationCard from './RecommendationCard';
import {
  CATEGORY_COLORS,
  getRecommendationCategoryColor,
  getRecommendationCategoryLabel,
  normalizeRecommendationCategory,
} from './RecommendationTags';

const CATEGORY_ORDER = Object.keys(CATEGORY_COLORS);

const AIVisibilityRecommendations = () => {
  const { recommendations, isLoading, error, cancel, mutate } = useCanaryFinalRecommendations();
  const [searchParams, setSearchParams] = useSearchParams();
  const recommendationId = searchParams.get('recommendationId');
  const [category, setCategory] = useState<string | null>(null);
  const [impact, setImpact] = useState('all');
  const selectedRecommendation =
    recommendations?.find((recommendation) => recommendation.id === recommendationId) ?? null;
  const orderedRecommendations = [...(recommendations ?? [])].sort(
    (first, second) =>
      (first.priority ?? Number.MAX_SAFE_INTEGER) - (second.priority ?? Number.MAX_SAFE_INTEGER),
  );
  const categories = [
    ...new Set(
      orderedRecommendations.flatMap((recommendation) =>
        [recommendation.gapArea, recommendation.scopeType]
          .filter((value): value is string => !!value)
          .map(normalizeRecommendationCategory),
      ),
    ),
  ].sort((first, second) => {
    const firstIndex = CATEGORY_ORDER.indexOf(first);
    const secondIndex = CATEGORY_ORDER.indexOf(second);
    return (firstIndex < 0 ? Infinity : firstIndex) - (secondIndex < 0 ? Infinity : secondIndex);
  });
  const visibleRecommendations = orderedRecommendations.filter((recommendation) => {
    const matchesCategory =
      category == null ||
      [recommendation.gapArea, recommendation.scopeType].some(
        (value) => value && normalizeRecommendationCategory(value) === category,
      );
    const matchesImpact = impact === 'all' || recommendation.impact?.toLowerCase() === impact;
    return matchesCategory && matchesImpact;
  });
  const highCount = orderedRecommendations.filter(
    (recommendation) => recommendation.impact?.toLowerCase() === 'high',
  ).length;
  const mediumCount = orderedRecommendations.filter(
    (recommendation) => recommendation.impact?.toLowerCase() === 'medium',
  ).length;
  const lowCount = orderedRecommendations.filter(
    (recommendation) => recommendation.impact?.toLowerCase() === 'low',
  ).length;
  const impactLabels: Record<string, string> = {
    all: t`All impacts`,
    high: t`High`,
    medium: t`Medium`,
    low: t`Low`,
  };
  const handleRecommendationChange = (id: string | null) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    if (id) {
      nextSearchParams.set('recommendationId', id);
    } else {
      nextSearchParams.delete('recommendationId');
    }
    setSearchParams(nextSearchParams, { replace: true });
  };
  const handleSolutionGenerated = async (recommendationId: string, solutionMarkdown: string) => {
    await cancel();
    mutate(
      (current) => {
        if (!current?.visualization.canaryFinalRecommendations) return current;

        const currentRecommendations = current.visualization.canaryFinalRecommendations;
        return {
          ...current,
          visualization: {
            ...current.visualization,
            canaryFinalRecommendations: {
              ...currentRecommendations,
              recommendations: currentRecommendations.recommendations.map((item) =>
                item.id === recommendationId ? { ...item, solutionMarkdown } : item,
              ),
            },
          },
        };
      },
      { revalidate: false },
    );
  };

  return (
    <div className="flex max-w-(--breakpoint-lg) flex-col gap-5 pt-6">
      <div>
        <Title level={2} variant="label">
          <Trans>{orderedRecommendations.length} Open recommendations.</Trans>
        </Title>
        <Text variant="label" weight="regular" className="block">
          <Trans>
            {highCount} High, {mediumCount} Medium and {lowCount} Low Impact Recommendations
          </Trans>
        </Text>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2" aria-label={t`Recommendation categories`}>
          <button
            type="button"
            aria-pressed={category == null}
            className={cn(
              'cursor-pointer rounded-full bg-gray-900 px-3 py-1 text-xs text-white focus-visible:ring-2 focus-visible:ring-green-400 focus-visible:outline-none',
              category == null && 'ring-2 ring-green-400 ring-offset-2',
            )}
            onClick={() => setCategory(null)}
          >
            <Trans>ALL ({orderedRecommendations.length})</Trans>
          </button>
          {categories.map((item) => (
            <button
              key={item}
              type="button"
              aria-pressed={category === item}
              className={cn(
                'cursor-pointer rounded-full px-3 py-1 text-xs focus-visible:ring-2 focus-visible:ring-green-400 focus-visible:outline-none',
                getRecommendationCategoryColor(item),
                category === item && 'ring-2 ring-green-400 ring-offset-2',
              )}
              onClick={() => setCategory(item)}
            >
              {getRecommendationCategoryLabel(item)}
            </button>
          ))}
        </div>
        <Select<string>
          value={impact}
          size="small"
          itemToStringLabel={(value) => impactLabels[value] ?? value}
          onValueChange={(value) => setImpact(value ?? 'all')}
        >
          <SelectTrigger className="w-40 rounded-full" aria-label={t`Impact`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <Trans>All impacts</Trans>
            </SelectItem>
            <SelectItem value="high">
              <Trans>High</Trans>
            </SelectItem>
            <SelectItem value="medium">
              <Trans>Medium</Trans>
            </SelectItem>
            <SelectItem value="low">
              <Trans>Low</Trans>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-5" aria-busy="true">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-24 rounded-2xl" />
          ))}
        </div>
      ) : error ? (
        <WidgetError message={<Trans>Unable to load recommendations.</Trans>} />
      ) : !visibleRecommendations.length ? (
        <Empty>
          <Trans>No recommendations available.</Trans>
        </Empty>
      ) : (
        <div className="flex flex-col gap-5">
          {visibleRecommendations.map((recommendation) => (
            <RecommendationCard
              key={recommendation.id}
              recommendation={recommendation}
              onClick={() => handleRecommendationChange(recommendation.id)}
            />
          ))}
        </div>
      )}

      <AIVisibilityRecommendationSheet
        recommendation={selectedRecommendation}
        onClose={() => handleRecommendationChange(null)}
        onSolutionGenerated={handleSolutionGenerated}
      />
    </div>
  );
};

export default AIVisibilityRecommendations;
